import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useEntity } from "@/hooks/useEntity";
import { chartColors } from "@/lib/theme/chartTheme";

const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export function SummaryTab() {
  const { orgId, familyId } = useTenant();
  const { entities } = useEntity();
  const [jurisdiction, setJurisdiction] = useState("MAS");
  const [entityFilter, setEntityFilter] = useState("all");
  const [period, setPeriod] = useState("2024-Q4");

  // Fetch holdings with their regulatory tags
  const { data: holdingsData } = useQuery({
    queryKey: ['regulatory-holdings', orgId, familyId, jurisdiction],
    queryFn: async () => {
      if (!orgId) return [];

      // Fetch holdings
      let holdingsQuery = supabase
        .from('holdings')
        .select('*, accounts!inner(entity_id)')
        .eq('org_id', orgId);

      if (familyId) {
        holdingsQuery = holdingsQuery.eq('family_id', familyId);
      }

      const { data: holdings, error: holdingsError } = await holdingsQuery;
      if (holdingsError) throw holdingsError;

      // Fetch regulatory tags for these holdings
      const holdingIds = holdings?.map(h => h.id) || [];
      if (holdingIds.length === 0) return [];

      const { data: holdingTags, error: tagsError } = await supabase
        .from('holding_regulatory_tags')
        .select('*, regulatory_tags(*)')
        .in('holding_id', holdingIds)
        .eq('jurisdiction', jurisdiction);

      if (tagsError) throw tagsError;

      // Merge holdings with their tags
      const tagsByHolding: Record<string, typeof holdingTags> = {};
      holdingTags?.forEach(ht => {
        if (!tagsByHolding[ht.holding_id]) {
          tagsByHolding[ht.holding_id] = [];
        }
        tagsByHolding[ht.holding_id].push(ht);
      });

      return holdings?.map(h => ({
        ...h,
        entityId: (h.accounts as { entity_id?: string })?.entity_id,
        tags: tagsByHolding[h.id] || [],
        hasValidTags: (tagsByHolding[h.id] || []).length > 0
      })) || [];
    },
    enabled: !!orgId
  });

  // Calculate KPIs and chart data
  const { totalHoldings, taggedHoldings, taggedPercentage, invalidTags, chartData, filteredHoldings } = useMemo(() => {
    if (!holdingsData) {
      return {
        totalHoldings: 0,
        taggedHoldings: 0,
        taggedPercentage: '0.0',
        invalidTags: 0,
        chartData: [],
        filteredHoldings: []
      };
    }

    // Filter by entity if selected
    let filtered = holdingsData;
    if (entityFilter !== 'all') {
      filtered = holdingsData.filter(h => h.entityId === entityFilter);
    }

    const total = filtered.length;
    const tagged = filtered.filter(h => h.hasValidTags).length;

    // Count invalid/expired tags
    const now = new Date();
    let invalid = 0;
    filtered.forEach(h => {
      h.tags.forEach((t: { regulatory_tags?: { effective_to?: string } }) => {
        if (t.regulatory_tags?.effective_to && new Date(t.regulatory_tags.effective_to) < now) {
          invalid++;
        }
      });
    });

    // Group by tag label for chart
    const byTagLabel: Record<string, number> = {};
    filtered.forEach(h => {
      const tagLabels = h.tags.map((t: { regulatory_tags?: { label?: string } }) => t.regulatory_tags?.label || 'Untagged');
      if (tagLabels.length === 0) {
        byTagLabel['Untagged'] = (byTagLabel['Untagged'] || 0) + Number(h.mv);
      } else {
        tagLabels.forEach((label: string) => {
          byTagLabel[label] = (byTagLabel[label] || 0) + Number(h.mv);
        });
      }
    });

    const chart = Object.entries(byTagLabel)
      .map(([name, value], idx) => ({
        name,
        value,
        color: CHART_COLORS[idx % CHART_COLORS.length]
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    return {
      totalHoldings: total,
      taggedHoldings: tagged,
      taggedPercentage: total > 0 ? ((tagged / total) * 100).toFixed(1) : '0.0',
      invalidTags: invalid,
      chartData: chart,
      filteredHoldings: filtered.slice(0, 10) // Show top 10
    };
  }, [holdingsData, entityFilter]);

  const jurisdictionsCovered = 3; // MAS, FATCA, CRS

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex gap-4">
        <Select value={jurisdiction} onValueChange={setJurisdiction}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select jurisdiction" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="MAS">MAS (Singapore)</SelectItem>
            <SelectItem value="FATCA">FATCA (US)</SelectItem>
            <SelectItem value="CRS">CRS (Global)</SelectItem>
          </SelectContent>
        </Select>

        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select entity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            {entities?.map((entity) => (
              <SelectItem key={entity.id} value={entity.id}>
                {entity.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="2024-Q4">Q4 2024</SelectItem>
            <SelectItem value="2024-Q3">Q3 2024</SelectItem>
            <SelectItem value="2024-Q2">Q2 2024</SelectItem>
            <SelectItem value="2024-Q1">Q1 2024</SelectItem>
            <SelectItem value="2024">2024 (Full Year)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Holdings Tagged</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{taggedPercentage}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {taggedHoldings} of {totalHoldings} holdings
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Invalid Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-warning">{invalidTags}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Requires attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Jurisdictions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{jurisdictionsCovered}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Active reporting
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ready for Export</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-8 w-8 text-success" />
              <div className="text-sm font-medium">Yes</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              All validations passed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Holdings by MAS Asset Class</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill={chartColors[0]}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => `$${(value / 1000000).toFixed(1)}M`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Holdings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tagged Holdings</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Holding</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Jurisdiction</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead className="text-right">Value (SGD)</TableHead>
                <TableHead>Last Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHoldings.map((holding) => (
                <TableRow key={holding.id}>
                  <TableCell className="font-medium">{holding.name || holding.symbol || 'Unknown'}</TableCell>
                  <TableCell>
                    {entities?.find(e => e.id === holding.entityId)?.name || 'Unknown Entity'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-[#0033A0] text-[#0033A0]">
                      {jurisdiction}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {holding.tags.length > 0 ? (
                        holding.tags.map((tag: { regulatory_tags?: { label?: string } }, idx: number) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {tag.regulatory_tags?.label || 'Unknown'}
                          </Badge>
                        ))
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          Untagged
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    ${((Number(holding.mv) || 0) / 1000000).toFixed(1)}M
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {holding.updated_at ? new Date(holding.updated_at).toLocaleDateString() : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
              {filteredHoldings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No holdings found. Upload statements to populate holdings data.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
